import React from "react";
import Reveal from "./Reveal";
import FinancingFlowAnimation from "./FinancingFlowAnimation";

const financingFacts = [
  {
    title: "Not equity.",
    text: "The financier owns no part of the film and no part of its future earnings. They hold a claim on receipts that ends when the premium is repaid, and it never comes back.",
  },
  {
    title: "Drawn at the rights holder’s request.",
    text: "The line is a ceiling, not a lump sum. The rights holder asks for money in draws, the financier funds each one, and repayment is owed on what was actually drawn.",
  },
  {
    title: "Both sides see the same figures.",
    text: "Drawn, repaid and outstanding, with every draw and repayment listed beneath, on the financier’s positions and the rights holder’s earnings alike. A statement can be downloaded or printed at any time.",
  },
  {
    title: "Scripts and budgets travel sealed.",
    text: "Documents attached to a request are encrypted before they leave your machine, to the one person you sent them to. Nobody in between can open them.",
  },
  {
    title: "Several financiers, one film.",
    text: "A title can carry more than one financing line at once, each with its own terms. Every receipt is divided across them in the same moment, and none is ranked above another. A later line can only be agreed out of the share the rights holder still has left, so a second financier never dilutes the first.",
  },
  {
    title: "Repaid from outside Fabstir too.",
    text: "A line need not wait for sales. The rights holder can repay it early from their Fabstir earnings or their own account, a third party can pay it down for them, and a financier can confirm money received outside Fabstir, such as a cinema release or a television sale. The line closes the moment the premium is met.",
  },
];

// Fabstir Entertainment film financing: a financier funds a title in draws
// and is repaid from the rights holder's share of every receipt, automatically.
export default function FinancingSection() {
  return (
    <div
      id="financing"
      className="mx-auto mt-24 max-w-7xl px-6 sm:mt-44 lg:px-8 scroll-mt-24"
    >
      <Reveal className="mx-auto max-w-2xl lg:text-center">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 lg:justify-center">
          <h2 className="text-base/7 font-semibold text-brand-lightpurple">
            Fabstir Entertainment &middot; Film financing
          </h2>
          <span className="rounded-full bg-brand-pink/20 px-3 py-0.5 text-xs font-semibold text-brand-pink ring-1 ring-inset ring-brand-pink/30">
            New
          </span>
        </div>
        <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-brand-pink sm:text-5xl lg:text-balance">
          Finance a film. Be repaid from every sale, automatically.
        </p>
        <p className="mt-6 text-lg/8 text-brand-gray">
          A financier and a rights holder agree a line, a repayment rate and a
          share of receipts between themselves, and the financier funds it in
          draws. From then on that share of everything the title earns, every
          sale and every distributor advance, goes to the financier until the
          money is repaid at the agreed premium. Nobody waits for a quarterly
          report and nobody chases one: each repayment is taken the moment a
          sale happens, and both sides watch the same figures.
        </p>
      </Reveal>

      <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 items-center gap-10 sm:mt-20 lg:max-w-none lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <FinancingFlowAnimation />
        </Reveal>
        <Reveal delay={150}>
          <h3 className="text-2xl font-semibold text-white sm:text-3xl">
            The platform&rsquo;s split is untouched. The financier is paid
            from the rights holder&rsquo;s side
          </h3>
          <p className="mt-4 text-base/7 text-gray-300">
            No platform signs the financing agreement and no deal is re-signed.
            Each purchase divides between platform and rights holder exactly as
            their deal says, and in the same instant an agreed percentage of the
            rights holder&rsquo;s share goes to the financier, until the money is
            repaid at the agreed premium. A distributor&rsquo;s advance is a
            rights-holder receipt like any other, so no single deal can take the
            money ahead of the person who funded the film.
          </p>
          <p className="mt-4 text-base/7 text-gray-300">
            The rights holder agrees a floor: while anything is outstanding,
            every new deal on the title must leave them at least a minimum share
            of each sale, which keeps the repayment source intact. Existing deals
            are unchanged. The financier owns no part of the film, and once the
            premium is repaid, or the financier waives the rest, the claim ends
            for good.
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-4 sm:mt-20 sm:grid-cols-2 lg:max-w-6xl lg:grid-cols-3">
        {financingFacts.map((fact, i) => (
          <Reveal key={fact.title} delay={(i % 3) * 120}>
            <div className="h-full rounded-xl bg-white/5 px-5 py-4 text-sm/6 text-gray-300 ring-1 ring-white/10">
              <span className="font-semibold text-white">{fact.title}</span>{" "}
              {fact.text}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
